import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { whatsappUrl } from "../../lib/leads";

const ROUTES = [
  {
    city: "Jaipur",
    distance: "335 km",
    time: "~5.5 hrs",
    desc: "The Pink City — forts, bazaars & royal palaces.",
    img: "https://images.pexels.com/photos/3581368/pexels-photo-3581368.jpeg?auto=compress&cs=tinysrgb&w=900&q=70",
  },
  {
    city: "Udaipur",
    distance: "250 km",
    time: "~4.5 hrs",
    desc: "The City of Lakes — romance on every ghat.",
    img: "https://images.pexels.com/photos/570031/pexels-photo-570031.jpeg?auto=compress&cs=tinysrgb&w=900&q=70",
  },
  {
    city: "Jaisalmer",
    distance: "285 km",
    time: "~5 hrs",
    desc: "The Golden City — dunes, camels & desert camps.",
    img: "https://images.pexels.com/photos/5308185/pexels-photo-5308185.jpeg?auto=compress&cs=tinysrgb&w=900&q=70",
  },
  {
    city: "Nakoda Ji",
    distance: "110 km",
    time: "~2.5 hrs",
    desc: "The revered Jain pilgrimage in Barmer.",
    img: "https://images.pexels.com/photos/19160105/pexels-photo-19160105.jpeg?auto=compress&cs=tinysrgb&w=900&q=70",
  },
];

const FALLBACK = "https://images.pexels.com/photos/35644782/pexels-photo-35644782.jpeg?auto=compress&cs=tinysrgb&w=900&q=70";

const RouteCard = ({ r, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <motion.a
      ref={ref}
      href={whatsappUrl(`Namaste! I want to book a cab from Jodhpur to ${r.city}.`)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`route-card-${r.city}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative block h-[420px] overflow-hidden rounded-3xl"
    >
      <motion.img
        style={{ y }}
        src={r.img}
        alt={`Jodhpur to ${r.city}`}
        loading="lazy"
        decoding="async"
        onError={(e) => { e.currentTarget.src = FALLBACK; }}
        className="absolute inset-0 h-[120%] w-full object-cover transition-transform duration-1000 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-jodhpur-ink via-jodhpur-ink/30 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-between p-7">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-xs font-medium text-white">
            <MapPin className="h-3.5 w-3.5" /> Jodhpur → {r.city}
          </span>
          <span className="h-10 w-10 rounded-full bg-white flex items-center justify-center transition-transform group-hover:rotate-45">
            <ArrowUpRight className="h-5 w-5 text-jodhpur-primary" />
          </span>
        </div>
        <div>
          <h3 className="font-display text-4xl sm:text-5xl text-white tracking-tight">{r.city}</h3>
          <p className="mt-2 text-white/80 text-sm max-w-xs">{r.desc}</p>
          <div className="mt-4 flex items-center gap-4 text-white/90 text-sm font-medium">
            <span>{r.distance}</span>
            <span className="h-1 w-1 rounded-full bg-jodhpur-accent" />
            <span>{r.time}</span>
          </div>
        </div>
      </div>
    </motion.a>
  );
};

export const RoutesSection = () => {
  return (
    <section id="routes" data-testid="routes-section" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <RevealOnScroll>
          <span className="text-xs uppercase tracking-[0.3em] text-jodhpur-accent font-semibold">Popular Outstation Routes</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-jodhpur-primary tracking-tight max-w-2xl">
            Where shall we <span className="italic">take you?</span>
          </h2>
        </RevealOnScroll>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {ROUTES.map((r, i) => (
            <RouteCard key={r.city} r={r} i={i} />
          ))}
        </div>

        <div className="mt-10">
          <p className="text-sm text-jodhpur-ink/55 mb-4">We also drive to many more destinations:</p>
          <div className="flex flex-wrap gap-2.5">
            {["Bikaner", "Ajmer", "Pushkar", "Mount Abu", "Barmer", "Alwar", "Bharatpur", "Banswara", "Balotra", "Baran", "Bhilwara", "Ramdevra"].map((c) => (
              <a
                key={c}
                href={whatsappUrl(`Namaste! I want a cab from Jodhpur to ${c}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`dest-${c}`}
                className="rounded-full border border-jodhpur-primary/15 px-4 py-2 text-sm font-medium text-jodhpur-ink/80 transition-all duration-300 hover:border-jodhpur-secondary hover:bg-jodhpur-primary hover:text-white"
              >
                Jodhpur → {c}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
